import { mockRequest } from "./mock/mockServer";
import { BASE_URL, PUBLIC_ENDPOINTS, QUIET_ENDPOINTS, REFRESH_ENDPOINT, USE_MOCK, isEndpointConnected, pathOf } from "./connection";

// THE single data-access entry point for the whole UI. No component talks to localStorage or the
// mock server directly — everything goes through apiClient(url, options).
//
// Two modes (see src/api/connection.js):
//  - Mock (default, and always in `npm test`): the in-browser mock server, unchanged.
//  - Backend (REACT_APP_USE_MOCK=false, set in .env.development): the Django backend at
//    REACT_APP_API_BASE_URL, authenticated with JWT: every request sends Authorization: Bearer <access>.
//    The access token is short-lived; on a 401 the client gets a new one from the refresh token
//    (POST /api/auth/token/refresh/) and retries once. No cookies, no CSRF. Endpoints the backend
//    doesn't have yet are refused here with code NOT_CONNECTED, without any network request.
export const STORAGE_KEYS = {
  // Mock mode: the session token. Backend mode: the JWT access token. In both modes "present" means "signed in".
  token: "chatbiz_token",
  // Backend mode only: the JWT refresh token (lasts as long as the sign-in, default 7 days).
  refreshToken: "chatbiz_refresh_token",
  tenant: "chatbiz_tenant",
  tenantName: "chatbiz_tenant_name",
  role: "chatbiz_role",
  userId: "chatbiz_user_id",
  userName: "chatbiz_user_name",
  userEmail: "chatbiz_user_email",
  teamRole: "chatbiz_team_role"
};

function readStorage(key) {
  try { return localStorage.getItem(key); } catch (e) { return null; }
}

function writeStorage(key, value) {
  try { localStorage.setItem(key, value); } catch (e) { /* storage unavailable */ }
}

function expireSession() {
  try {
    localStorage.removeItem(STORAGE_KEYS.token);
    localStorage.removeItem(STORAGE_KEYS.refreshToken);
  } catch (e) { /* storage unavailable */ }
  window.dispatchEvent(new CustomEvent("chatbizz_session_expired"));
}

/** The tokens currently stored (after any automatic renewal). */
export const readTokens = () => ({ token: readStorage(STORAGE_KEYS.token), refreshToken: readStorage(STORAGE_KEYS.refreshToken) });

/** Django routes end with "/": /api/team/members/12 -> /api/team/members/12/ (query string kept). */
function withTrailingSlash(url) {
  const [path, query] = String(url).split(/\?(.*)/s);
  const fixed = path.endsWith("/") ? path : `${path}/`;
  return query !== undefined ? `${fixed}?${query}` : fixed;
}

/** DRF errors: {field: ["msg", ...]} or {field: "msg"} -> {field: "msg"} (what the forms read). */
function flattenFieldErrors(errors) {
  if (!errors || typeof errors !== "object" || Array.isArray(errors)) return {};
  return Object.fromEntries(Object.entries(errors).map(([field, value]) => [
    field, Array.isArray(value) ? String(value[0] ?? "") : (typeof value === "object" ? JSON.stringify(value) : String(value))
  ]));
}

function notConnectedError(method, url) {
  const error = new Error("This feature isn't connected to the backend yet.");
  error.status = 501;
  error.code = "NOT_CONNECTED";
  error.details = { method, path: pathOf(url) };
  return error;
}

async function mockCall(url, options) {
  const { tenantId, role, ...fetchOptions } = options;
  const token = readStorage(STORAGE_KEYS.token);
  const tenant = tenantId === undefined ? readStorage(STORAGE_KEYS.tenant) : tenantId;
  const config = {
    ...fetchOptions,
    headers: {
      "Content-Type": "application/json",
      ...(fetchOptions.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(tenant ? { "X-Tenant-ID": tenant } : {}),
      "X-Mock-Role": role || readStorage(STORAGE_KEYS.role) || "viewer"
    }
  };

  let status;
  let data;
  try {
    ({ status, body: data } = await mockRequest(url, config));
  } catch (cause) {
    const error = new Error("Network request failed");
    error.code = "NETWORK_ERROR";
    error.cause = cause;
    throw error;
  }

  if (status < 200 || status >= 300) {
    if (status === 401 && data?.error?.code === "SESSION_EXPIRED") expireSession();
    const error = new Error(data?.error?.message || data?.message || "Request failed");
    error.status = status;
    error.code = data?.error?.code;
    error.details = data?.error?.details;
    error.requestId = data?.requestId;
    error.data = data;
    throw error;
  }
  return data;
}

// One renewal at a time: if several requests hit 401 together they all wait for the same refresh.
let refreshing = null;

/** New access token from the stored refresh token; resolves true on success. */
function renewAccessToken() {
  const refresh = readStorage(STORAGE_KEYS.refreshToken);
  if (!refresh) return Promise.resolve(false);
  if (!refreshing) {
    refreshing = fetch(`${BASE_URL}${withTrailingSlash(REFRESH_ENDPOINT)}`, {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ refresh })
    })
      .then((res) => res.json().catch(() => ({})).then((body) => {
        const access = res.ok && body?.data?.access;
        if (access) writeStorage(STORAGE_KEYS.token, access);
        return !!access;
      }))
      .catch(() => false)
      .finally(() => { refreshing = null; });
  }
  return refreshing;
}

function send(url, method, fetchOptions, isPublic) {
  const access = readStorage(STORAGE_KEYS.token);
  return fetch(`${BASE_URL}${withTrailingSlash(url)}`, {
    ...fetchOptions,
    method,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(fetchOptions.headers || {}),
      ...(!isPublic && access ? { Authorization: `Bearer ${access}` } : {})
    }
  });
}

async function backendCall(url, options) {
  // tenantId / role are mock-only overrides: the backend derives both from the token's user.
  const { tenantId, role, ...fetchOptions } = options; // eslint-disable-line no-unused-vars
  const method = String(fetchOptions.method || "GET").toUpperCase();
  const path = pathOf(url);
  const isPublic = PUBLIC_ENDPOINTS.test(path);

  if (!isEndpointConnected(method, url)) throw notConnectedError(method, url);

  let response;
  try {
    response = await send(url, method, fetchOptions, isPublic);
    // Access token expired / ended: renew it once and retry.
    if (response.status === 401 && !isPublic && await renewAccessToken()) {
      response = await send(url, method, fetchOptions, isPublic);
    }
  } catch (cause) {
    const error = new Error("Can't reach the server. Check that the backend is running and try again.");
    error.code = "NETWORK_ERROR";
    error.cause = cause;
    throw error;
  }

  const data = await response.json().catch(() => ({}));
  const { status } = response;

  if (status >= 200 && status < 300) return data;

  // Still 401 after trying to renew = the sign-in has ended (logged out elsewhere, password reset,
  // deactivated, or older than 7 days).
  if (status === 401 && !isPublic && !QUIET_ENDPOINTS.test(path) && readStorage(STORAGE_KEYS.token)) expireSession();

  const fields = flattenFieldErrors(data?.errors);
  const error = new Error(data?.message || "Request failed");
  error.status = status;
  error.code = data?.code || (status === 401 ? "SESSION_EXPIRED" : undefined);
  error.details = { fields, ...(fields.email ? { email: fields.email } : {}) };
  error.requestId = data?.request_id;
  error.data = data;
  throw error;
}

/**
 * Shared API client.
 *  - always resolves to the response body ({ success, data, ... }); services read `.data`
 *  - always throws a normalised Error: { status, code, message, details: { fields }, requestId, data }
 *  - an expired session clears the stored token and fires `chatbizz_session_expired` (the app routes to /session-expired)
 */
export async function apiClient(url, options = {}) {
  return USE_MOCK ? mockCall(url, options) : backendCall(url, options);
}
